import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Search, Star, MapPin, Hammer } from "lucide-react";
import { useSEO } from "../lib/useSEO";

const CATEGORY_INTROS = {
  "All": "Browse every vetted contractor in the Columbia Valley, from Sparwood to Canal Flats. Search by trade, location or business name to find the right local pro for your next home project.",
  "Plumbing": "Local Columbia Valley plumbers for emergency callouts, new builds, bathroom renos, water-heater installs and seasonal cabin shut-offs. Every pro listed is part of the valley community.",
  "Electrical": "Licensed electricians serving Invermere, Radium, Fairmont and surrounding areas. From panel upgrades and EV chargers to lighting, wiring and inspections.",
  "Roofing": "Mountain-tested roofers handling everything from snow-load metal roofs to asphalt shingle replacement, leak diagnosis and seasonal maintenance across the Columbia Valley.",
  "Landscaping": "Valley landscapers, lawn-care crews and hardscape specialists for everything from spring clean-up to full backyard transformations.",
  "Renovation": "Full-service renovation contractors for kitchen, bathroom and whole-home remodels — cabin retrofits, modern additions and heritage restorations.",
  "HVAC": "HVAC installers and service techs who know what mountain winters demand. Furnace, heat-pump, A/C, ductless mini-split and indoor air-quality specialists.",
  "Painting": "Interior and exterior painters for residential and commercial projects. Long-lasting finishes that stand up to Columbia Valley sun, snow and wind.",
  "Flooring": "Hardwood, laminate, tile, luxury vinyl and refinishing specialists serving the Columbia Valley. Get quotes from local installers who know mountain homes.",
  "Cleaners": "Trusted residential cleaners, vacation-rental turnover crews and post-construction specialists across the valley. From recurring maintenance to deep cleans.",
  "Photography": "Local photographers for weddings, families, real estate, products and Columbia Valley adventure shoots. Browse portfolios and book directly.",
  "Private Home Chef": "Private chefs for dinner parties, vacation rentals, special occasions and weekly meal prep. Bring restaurant-grade cooking to your Columbia Valley home.",
  "Child Care": "Local nannies, babysitters and after-school care providers serving Columbia Valley families. Browse vetted profiles and connect directly.",
};

const ALL_CATS = ["All","Plumbing","Electrical","Roofing","Landscaping","Renovation","HVAC","Painting","Flooring","Cleaners","Photography","Private Home Chef","Child Care"];

export default function Directory() {
  const [params, setParams] = useSearchParams();
  const initialCat = params.get("category") || "All";
  const [cat, setCat] = useState(initialCat);
  const [q, setQ] = useState("");
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: cat === "All" ? "Contractor Directory" : `${cat} Contractors in the Columbia Valley`,
    description: CATEGORY_INTROS[cat] || CATEGORY_INTROS["All"],
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/contractors`, { params: { category: cat, q } });
      setContractors(data);
    } finally { setLoading(false); }
  }, [cat, q]);

  useEffect(() => { fetchData(); }, [cat, fetchData]);
  useEffect(() => {
    const id = setTimeout(fetchData, 300);
    return () => clearTimeout(id);
  }, [q, fetchData]);

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-10 py-12">
      <div className="mb-10">
        <div className="overline text-[#B85C47] mb-3">Contractor Directory</div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-3" style={{ fontFamily: "Outfit" }}>
          {cat === "All" ? "Find your pro" : `${cat} contractors`}
        </h1>
        <p className="text-stone-600 max-w-3xl leading-relaxed">{CATEGORY_INTROS[cat] || CATEGORY_INTROS["All"]}</p>
      </div>

      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            data-testid="contractor-search-input"
            placeholder="Search by name, location or specialty…"
            value={q}
            onChange={e => setQ(e.target.value)}
            className="pl-10 h-12 bg-white"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-10">
        {ALL_CATS.map(c => (
          <button
            key={c}
            data-testid={`category-${c.toLowerCase()}-filter`}
            onClick={() => { setCat(c); setParams(c === "All" ? {} : { category: c }); }}
            className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors ${
              cat === c ? "bg-[#314A3E] text-[#EAB89F]" : "bg-[#EAE5D9] text-[#314A3E] hover:bg-[#dcd5c2]"
            }`}
          >{c}</button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-20 text-stone-500">Loading…</div>
      ) : contractors.length === 0 ? (
        <div className="text-center py-20 bg-white border border-[#E2DCD0] rounded-xl">
          <Hammer className="w-10 h-10 mx-auto text-stone-300 mb-4" />
          <div className="text-stone-600">No contractors found{cat !== "All" ? ` in ${cat}` : ""}.</div>
          <div className="text-sm text-stone-400 mt-1">Be the first — <Link to="/signup" className="text-[#B85C47] font-semibold">join as a pro</Link>.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {contractors.map((c, i) => (
            <Link
              key={c.user_id}
              to={`/contractors/${c.user_id}`}
              data-testid={`contractor-card-${c.user_id}`}
              className="bg-white border border-[#E2DCD0] rounded-xl overflow-hidden hover:-translate-y-1 hover:shadow-md transition-all fade-up"
              style={{ animationDelay: `${i * 40}ms` }}
            >
              <div className="h-32 wood-grain relative overflow-hidden">
                {c.cover_image && <img src={c.cover_image} alt="" className="w-full h-full object-cover" />}
                <div className="absolute top-3 left-3 px-2.5 py-1 bg-white/95 rounded-full text-[10px] uppercase tracking-[0.15em] font-bold text-[#314A3E]">
                  {c.category}
                </div>
                {c.is_featured && (
                  <div className="absolute top-3 right-3 px-2.5 py-1 bg-[#B85C47] text-white rounded-full text-[10px] uppercase tracking-[0.15em] font-bold">
                    Featured
                  </div>
                )}
              </div>
              <div className="p-5">
                <h3 className="font-semibold text-lg tracking-tight" style={{ fontFamily: "Outfit" }}>{c.business_name}</h3>
                <div className="flex items-center gap-1 text-xs text-stone-500 mt-1">
                  <MapPin className="w-3 h-3" /> {c.location}
                </div>
                <p className="text-sm text-stone-600 mt-3 line-clamp-2">{c.bio}</p>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#E2DCD0]">
                  <div className="flex items-center gap-1">
                    <Star className="w-3.5 h-3.5 fill-[#B85C47] text-[#B85C47]" />
                    <span className="text-sm font-semibold">{c.avg_rating || "—"}</span>
                    <span className="text-xs text-stone-400">({c.review_count})</span>
                  </div>
                  {c.hourly_rate ? <div className="text-xs text-stone-500">From <span className="font-semibold text-[#314A3E]">${c.hourly_rate}/hr</span></div> : null}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
